// Purchase history tab table — used on Customer 360. Thin wrapper around the
// shared ReportTable so the tab chrome matches other report/ledger sections.

import { useMemo } from 'react';
import type { ColumnDef } from '@bbpos/ui';
import { Badge } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ReportTable } from '@/features/reports';
import type { CustomerPurchaseRow } from '../types';
import { formatDateTime } from '../format';

type Row = CustomerPurchaseRow;

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
  {
    key: 'bill_number',
    header: 'Bill #',
    render: (_v, row) => (
      <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">{row.bill_number}</span>
    ),
  },
  {
    key: 'created_at',
    header: 'Date',
    render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-300 tabular-nums">{formatDateTime(row.created_at)}</span>,
  },
  {
    key: 'location_name',
    header: 'Location',
    render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-300">{row.location_name ?? '—'}</span>,
  },
  {
    key: 'item_count',
    header: 'Items',
    numeric: true,
    render: (_v, row) => <span className="font-mono tabular-nums">{row.item_count}</span>,
  },
  {
    key: 'payment_method',
    header: 'Payment',
    render: (_v, row) => <Badge variant="info" size="sm">{row.payment_method}</Badge>,
  },
  {
    key: 'grand_total',
    header: 'Total',
    numeric: true,
    render: (_v, row) => <span className="text-sm font-semibold font-mono tabular-nums">{formatPaise(row.grand_total + (row.rounding_adjustment ?? 0), currency)}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => {
      const v = row.status.toLowerCase();
      const variant = v === 'completed' ? 'success' : v === 'voided' ? 'voided' : v === 'returned' ? 'returned' : 'info';
      return <Badge variant={variant} size="sm">{row.status}</Badge>;
    },
  },
  ];
}

export interface CustomerPurchaseTableProps {
  rows: Row[];
  total?: number;
  page: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
}

export function CustomerPurchaseTable({ rows, total, page, loading, onPageChange }: CustomerPurchaseTableProps) {
  const currency = useTenantCurrency();
  const columns = useMemo(() => makeColumns(currency), [currency]);
  return (
    <ReportTable<Row>
      title="Purchase History"
      subtitle="Every bill this customer has paid."
      columns={columns}
      data={rows}
      loading={loading}
      totalCount={total}
      currentPage={page}
      onPageChange={onPageChange}
      emptyTitle="No purchases yet"
      emptyDescription="Once this customer makes a purchase, their bills will appear here."
    />
  );
}
